import React, {Component, ErrorInfo, ReactNode} from 'react'
import i18next from 'i18next'
import {Trans} from 'react-i18next'
import {Result} from 'antd'
import {Button} from '@/components'
import {urls} from '@/constants/urls'
import {isLoadChunkError, getErrorType, getErrorTypeLabel} from './utils'
import {ErrorType} from './types'
import {IErrorBoundaryStore} from '@/components/containers/ErrorBoundary/IErrorBoundaryStore'
import {captureSentryError, getErrorMessage} from '@/services/sentry-helper'

interface ErrorBoundaryProps {
    children: ReactNode
    errorBoundaryStore: IErrorBoundaryStore
}

interface ErrorBoundaryState {
    hasError: boolean
    errorType: ErrorType | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    private errorBoundaryStore: IErrorBoundaryStore = null

    constructor(props: ErrorBoundaryProps) {
        super(props)
        this.state = {hasError: false, errorType: null}
        this.errorBoundaryStore = this.props.errorBoundaryStore
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        // Forcely set error type to 'LoadChunk' if error is ChunkLoadError
        if (isLoadChunkError(error)) return {hasError: false, errorType: 'LoadChunk'}

        const errorType = getErrorType(error)

        captureSentryError({
            name: `ErrorBoundary ${getErrorTypeLabel(errorType)} (derived)`,
            description: getErrorMessage(error, 'Error caught in getDerivedStateFromError'),
            fingerprint: ['error-boundary-derived', errorType],
            extra: {
                errorType,
                errorName: error?.name,
                errorCode: (error as Error & {code?: string})?.code,
                stack: error?.stack,
                pathname: typeof window !== 'undefined' ? window.location.pathname : null
            },
            error
        })

        // fallback will render only for other errors
        return {hasError: true, errorType}
    }

    componentDidCatch(err: Error, errorInfo: ErrorInfo) {
        this.props.errorBoundaryStore.handleCaughtError(err, errorInfo, this.state.errorType)
    }

    render() {
        if (this.state.hasError) {
            return (
                <Result
                    status='error'
                    title={
                        <Trans
                            i18nKey='ErrorBoundary.errors.ourSupportTeamIsHereToHelp'
                            components={{
                                a: (
                                    <a
                                        href={urls.supportFormUrl}
                                        target={'_blank'}
                                        rel={'noreferrer'}
                                    />
                                )
                            }}
                        />
                    }
                    extra={
                        <Button
                            type='primary'
                            key='console'
                            onClick={() =>
                                this.errorBoundaryStore?.onGoHomeClick(
                                    this.state.errorType === 'AuthError'
                                )
                            }
                        >
                            {i18next.t('ErrorBoundary.buttons.home')}
                        </Button>
                    }
                />
            )
        }
        return this.props.children
    }
}
